import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, DataTable, PageError, PageLoading } from "../components/index.js";
import { SourceChip, StatusChip } from "../components/AdminForm.jsx";
import { useApi, useApiAction } from "../hooks/useApi.js";
import { timeAgo } from "../lib/time.js";

/**
 * Admin client list. Every client BMG manages, with source chips, user count,
 * last sync, and an overall status. "Add Client" opens the new-client wizard;
 * a row's actions deep-link to the client detail page or trigger a sync.
 */
export function AdminClients() {
  const navigate = useNavigate();
  const api = useApiAction();
  const { data, loading, error: loadError, reload } = useApi("/api/clients");
  // API returns creation order; the list reads better alphabetical (same
  // locale-aware sort as the client switcher).
  const clients = (data?.clients || [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  const [syncingId, setSyncingId] = useState(null);
  const [actionError, setActionError] = useState(null);

  async function triggerSync(id) {
    setSyncingId(id);
    setActionError(null);
    try {
      // Admins target a specific client via the view-as override (?clientId).
      await api(`/api/sync/run?clientId=${id}`, { method: "POST" });
      await reload({ silent: true });
    } catch (e) {
      setActionError(e);
    } finally {
      setSyncingId(null);
    }
  }

  const error = loadError || actionError;
  if (error) return <PageError error={error} onRetry={() => { setActionError(null); reload(); }} />;
  if (loading) return <PageLoading />;

  const liveSources = (c) => (c.dataSources || []).filter((d) => d.status !== "disconnected");
  const lastSyncOf = (c) =>
    (c.dataSources || [])
      .map((d) => d.lastSync)
      .filter(Boolean)
      .sort()
      .pop() || null;

  return (
    <div className="space-y-5 max-w-[1200px]">
      <Card>
        <CardHeader
          title="Clients"
          subtitle={`${clients.length} ${clients.length === 1 ? "client" : "clients"} managed by BMG`}
          right={
            <button
              type="button"
              onClick={() => navigate("/admin/clients/new")}
              className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity"
            >
              + Add Client
            </button>
          }
        />
        {clients.length > 0 && (
          <DataTable
            columns={[
              {
                key: "name", label: "Client",
                render: (r) => (
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/clients/${r.id}`)}
                    className="font-medium text-text-primary hover:text-accent transition-colors text-left"
                  >
                    {r.name}
                  </button>
                ),
              },
              { key: "plan", label: "Plan", render: (r) => <span className="capitalize">{r.plan}</span> },
              {
                key: "sources", label: "Data Sources",
                render: (r) => {
                  const live = liveSources(r);
                  if (live.length === 0) return <span className="text-text-muted">—</span>;
                  return (
                    <span className="inline-flex flex-wrap gap-1">
                      {live.slice(0, 4).map((d) => <SourceChip key={d.type}>{d.type}</SourceChip>)}
                      {live.length > 4 && <span className="text-[10px] text-text-muted">+{live.length - 4}</span>}
                    </span>
                  );
                },
              },
              { key: "users", label: "Users", align: "right", render: (r) => r._count?.users ?? "—" },
              { key: "lastSync", label: "Last Sync", align: "right", render: (r) => timeAgo(lastSyncOf(r)) },
              { key: "status", label: "Status", render: (r) => <StatusChip dataSources={r.dataSources} /> },
              {
                key: "actions", label: "", align: "right",
                render: (r) => (
                  <span className="inline-flex items-center gap-3 justify-end whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => navigate(`/clients/${r.id}`)}
                      className="text-[12px] text-accent hover:underline font-medium"
                    >
                      Open cockpit
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/clients/${r.id}`)}
                      className="text-[12px] text-accent hover:underline font-medium"
                    >
                      View
                    </button>
                    <button
                      type="button"
                      onClick={() => triggerSync(r.id)}
                      disabled={syncingId === r.id}
                      className="text-[12px] text-text-secondary hover:text-text-primary disabled:opacity-50"
                    >
                      {syncingId === r.id ? "Syncing…" : "Sync"}
                    </button>
                  </span>
                ),
              },
            ]}
            rows={clients}
          />
        )}
        {clients.length === 0 && (
          <div className="px-5 py-10 text-center text-[13px] text-text-muted">
            No clients yet. Click <span className="font-medium text-text-secondary">+ Add Client</span> to onboard one.
          </div>
        )}
      </Card>
    </div>
  );
}
